import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getAllActivePosts } from '../services/api';
import { Post } from '../types';
import { ROUTES } from '../constants';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Header } from '../components/Header';
import { ProviderTemplateModal } from '../components/ProviderTemplateModal';
import PostCard from '../components/PostCard';

const AllPostsPage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (user.role !== 'provider') {
      navigate(ROUTES.HOME_PAGE);
      return;
    }

    fetchPosts();
  }, [user, loading, navigate]);

  const fetchPosts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllActivePosts();
      if (response.success) {
        setPosts(response.posts);
      } else {
        setError(response.error || 'Failed to load posts');
      }
    } catch (err) {
      setError('An error occurred while loading posts');
      console.error('Error fetching posts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactPost = (post: Post) => {
    setSelectedPost(post);
    setIsTemplateModalOpen(true);
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header theme="green" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Browse Care Requests</h1>
          <p className="mt-2 text-gray-600">
            Connect with service users who need your care services
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchPosts}
              className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Try again
            </button>
          </div>
        )}

        {posts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center">
                <FileText className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">No active care requests</h3>
              <p className="text-gray-600 max-w-md">
                There are currently no active care request posts. Check back later to find
                opportunities to connect with service users who need your expertise.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <PostCard
                key={post.postId}
                post={post}
                onContact={handleContactPost}
                viewMode="provider"
              />
            ))}
          </div>
        )}
      </div>

      {selectedPost && user?.providerId && (
        <ProviderTemplateModal
          isOpen={isTemplateModalOpen}
          onClose={() => {
            setIsTemplateModalOpen(false);
            setSelectedPost(null);
          }}
          post={selectedPost}
          providerId={user.providerId}
        />
      )}
    </div>
  );
};

export default AllPostsPage;
